import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ILanguageService, Language } from 'continity-shared-ui';

/**
 * Lean `ILanguageService` for the shared shell.
 *
 * Backs the shared-ui `app-language-selector` (the `LANGUAGE_SERVICE` token, wired in
 * {@link provideShell}) so the shell apps render the SAME language dropdown as the main
 * portal — instead of a hand-rolled button row — WITHOUT pulling the portal's
 * LanguageService (which also does a DB preference write + `?lang` URL sync + RTL
 * handling that the shell apps don't need). No shared-ui change required: we only
 * provide the documented extension token.
 *
 * Persists to the same `app_language` localStorage key the {@link provideShell}
 * initializer restores from on reload, so a language pick survives a refresh.
 */
@Injectable({ providedIn: 'root' })
export class ShellLanguageService implements ILanguageService {
  private readonly STORAGE_KEY = 'app_language';

  /** The shared-ui `Language` type also allows `'ar'`; the shell offers de/en/fr/it. */
  readonly languages: { code: Language; name: string }[] = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'fr', name: 'Français' },
    { code: 'it', name: 'Italiano' },
  ];

  private langSubject: BehaviorSubject<Language>;
  readonly currentLanguage$: Observable<Language>;

  constructor(private translate: TranslateService) {
    const current = (this.translate.currentLang ||
      this.translate.getDefaultLang() ||
      'en') as Language;
    this.langSubject = new BehaviorSubject<Language>(current);
    this.currentLanguage$ = this.langSubject.asObservable();
    this.translate.onLangChange.subscribe((e) =>
      this.langSubject.next(e.lang as Language)
    );
  }

  changeLanguage(lang: Language): void {
    this.translate.use(lang);
    localStorage.setItem(this.STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  }

  getCurrentLang(): Language {
    return (this.translate.currentLang || this.langSubject.value) as Language;
  }
}
